import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import AudioRecorder from './AudioRecorder';
import { Plus, GraduationCap, X } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1'];

/**
 * Teacher-only panel that publishes a Hörverstehen (listening) material from
 * the ExercisesPage in one click. Uses the shared AudioRecorder component so
 * the teacher can record directly from mobile OR upload an audio file.
 *
 * Materials created here are saved with `category: 'listening'` and therefore
 * appear to the student's class under Material → Hören.
 */
export default function TeacherListeningUpload() {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [levels, setLevels] = useState(['A2']);
  const [groupId, setGroupId] = useState('');
  const [groups, setGroups] = useState([]);
  const [pendingAudio, setPendingAudio] = useState(null);

  useEffect(() => {
    axios.get(`${API}/groups`, { withCredentials: true })
      .then(r => setGroups(r.data || []))
      .catch(() => {});
  }, []);

  const toggleLevel = (lvl) => setLevels(ls => ls.includes(lvl) ? ls.filter(l => l !== lvl) : [...ls, lvl]);

  const publish = async () => {
    if (!title.trim()) return toast.error('Titel erforderlich');
    if (!pendingAudio) return toast.error('Audio hinzufügen (aufnehmen oder hochladen)');
    if (!levels.length) return toast.error('Mindestens ein Niveau');
    try {
      await axios.post(`${API}/materials`, {
        title: title.trim(),
        description: '',
        levels,
        category: 'listening',
        content_url: pendingAudio.url,
        content_type: (pendingAudio.name || '').split('.').pop().toLowerCase(),
        files: [pendingAudio],
        group_id: groupId || '',
      }, { withCredentials: true });
      toast.success('Hörmaterial veröffentlicht');
      setTitle(''); setPendingAudio(null); setGroupId(''); setLevels(['A2']);
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler');
    }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-2 mt-2 px-4 py-2 rounded-lg bg-[#2F6B50] text-white font-semibold hover:bg-[#245140] text-sm"
        data-testid="teacher-listening-open"
      >
        <Plus size={16} weight="bold" /> Hörmaterial hochladen
      </button>
    );
  }

  return (
    <div className="border border-[#2F6B50]/40 bg-[#F3F9F5] rounded-xl p-4 space-y-3" data-testid="teacher-listening-form">
      <div className="flex items-center justify-between">
        <h3 className="font-heading font-semibold text-[#292524] flex items-center gap-2">
          <GraduationCap size={18} weight="duotone" className="text-[#2F6B50]" /> Hörmaterial veröffentlichen
        </h3>
        <button onClick={() => setOpen(false)} className="text-[#78716C] hover:text-[#292524]" data-testid="teacher-listening-close">
          <X size={16} />
        </button>
      </div>
      <input
        value={title}
        onChange={e => setTitle(e.target.value)}
        placeholder="Titel (z.B. Dialog im Café)"
        className="w-full border border-stone-300 rounded px-3 py-2 text-sm"
        data-testid="teacher-listening-title"
      />
      <div>
        <p className="text-xs font-medium text-[#57534E] mb-1.5">Niveaus</p>
        <div className="flex flex-wrap gap-1.5">
          {LEVELS.map(lvl => {
            const on = levels.includes(lvl);
            return (
              <button key={lvl} type="button" onClick={() => toggleLevel(lvl)}
                className={`px-3 py-1.5 text-xs font-semibold rounded-full border ${on ? 'bg-[#2F6B50] text-white border-[#2F6B50]' : 'bg-white text-[#57534E] border-stone-300'}`}>
                {lvl}
              </button>
            );
          })}
        </div>
      </div>
      <div>
        <p className="text-xs font-medium text-[#57534E] mb-1.5">Klasse (optional)</p>
        <select
          value={groupId}
          onChange={e => setGroupId(e.target.value)}
          className="w-full border border-stone-300 rounded px-3 py-2 text-sm bg-white"
          data-testid="teacher-listening-group"
        >
          <option value="">— Alle Klassen —</option>
          {groups.map(g => <option key={g.group_id} value={g.group_id}>{g.name} · {g.level}</option>)}
        </select>
      </div>
      <AudioRecorder
        testId="teacher-listening-audio"
        maxSeconds={600}
        onUploaded={(f) => { setPendingAudio(f); toast.success('Audio bereit'); }}
      />
      {pendingAudio && (
        <p className="text-xs text-emerald-700 font-semibold">✓ Audio bereit: {pendingAudio.name}</p>
      )}
      <button
        onClick={publish}
        disabled={!pendingAudio || !title.trim()}
        className="w-full sm:w-auto inline-flex items-center gap-2 bg-[#AB1C24] hover:bg-[#8A151C] text-white px-4 py-2 rounded-lg font-semibold disabled:opacity-50"
        data-testid="teacher-listening-publish"
      >
        <Plus size={16} weight="bold" /> Veröffentlichen
      </button>
    </div>
  );
}
